import { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Tabs, Empty, Button, Tag, Popconfirm, Badge, App } from 'antd';
import { useAuth } from '../../context/AuthContext';
import { fetchCoupons } from '../../api';
import './Account.scss';

function Account() {
  const { user, paidServices, removePaidService } = useAuth();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);

  const tabParam = searchParams.get('tab');
  const TAB_KEYS = ['paid', 'coupons'];
  const activeTab = TAB_KEYS.includes(tabParam) ? tabParam : 'paid';

  // 未登录直接引导回首页
  useEffect(() => {
    if (!user) {
      message.warning('请先登录后再查看个人中心');
      navigate('/', { replace: true });
    }
  }, [user, navigate, message]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchCoupons()
      .then((list) => {
        if (alive) setCoupons(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (alive) setCoupons([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const handleTabChange = (key) => {
    setSearchParams(key === 'paid' ? {} : { tab: key });
  };

  const handleRemovePaid = (svc) => {
    removePaidService(svc.id);
    message.success(`已退订「${svc.name}」`);
  };

  const couponTypeColor = useMemo(
    () => ({ 满减: 'red', 折扣: 'orange', 免息: 'green' }),
    []
  );

  /* 我的付费服务 */
  const paidPane = (
    <div className="account-pane">
      {paidServices && paidServices.length > 0 ? (
        <div className="acc-paid-grid">
          {paidServices.map((s) => (
            <div className="acc-paid-card" key={s.id}>
              <div className="acc-paid-icon">{s.icon}</div>
              <div className="acc-paid-info">
                <div className="acc-paid-name">{s.name}</div>
                {s.desc && <div className="acc-paid-desc">{s.desc}</div>}
                <div className="acc-paid-bottom">
                  <span className="acc-paid-price">
                    ¥{s.price}
                    <em>/{s.period}</em>
                  </span>
                  <Popconfirm
                    title="确认退订该服务？"
                    okText="退订"
                    cancelText="保留"
                    onConfirm={() => handleRemovePaid(s)}
                  >
                    <Button size="small" danger>
                      退订
                    </Button>
                  </Popconfirm>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Empty description="你还没有认购任何付费服务">
          <Button type="primary" onClick={() => navigate('/')}>
            去首页看看尊享服务
          </Button>
        </Empty>
      )}
    </div>
  );

  const couponsPane = (
    <div className="account-pane">
      {loading ? (
        <div className="acc-loading">加载中…</div>
      ) : coupons.length > 0 ? (
        <div className="acc-coupon-grid">
          {coupons.map((c) => (
            <div className="acc-coupon" key={c.id}>
              <div className="acc-coupon-left">
                <div className="acc-coupon-value">{c.value}</div>
                <Tag color={couponTypeColor[c.type] || 'blue'}>{c.type}</Tag>
              </div>
              <div className="acc-coupon-right">
                <div className="acc-coupon-name">{c.name}</div>
                <div className="acc-coupon-cond">{c.cond}</div>
                <div className="acc-coupon-expire">有效期至 {c.expire}</div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Empty description="暂无可用优惠券" />
      )}
    </div>
  );

  if (!user) return null;

  return (
    <div className="account-page">
      <div className="account-hero">
        <div className="account-avatar">{(user.name || 'U').slice(0, 1)}</div>
        <div className="account-meta">
          <h1>{user.name}</h1>
          <p>{user.phone || '欢迎回到好美意个人中心'}</p>
        </div>
      </div>

      {/* 会员中心概览卡片（点击切换到对应 Tab） */}
      <div className="member-grid">
        <button
          type="button"
          className={`member-card${activeTab === 'paid' ? ' active' : ''}`}
          onClick={() => handleTabChange('paid')}
        >
          <Badge count={paidServices ? paidServices.length : 0} size="small" offset={[8, -2]}>
            <div className="member-icon">💎</div>
          </Badge>
          <h3>我的付费服务</h3>
          <p>查看已认购的尊享增值服务</p>
          <span className="member-go">查看详情 →</span>
        </button>
        <button
          type="button"
          className={`member-card${activeTab === 'coupons' ? ' active' : ''}`}
          onClick={() => handleTabChange('coupons')}
        >
          <Badge count={coupons.length} size="small" offset={[8, -2]}>
            <div className="member-icon">🎫</div>
          </Badge>
          <h3>我的优惠券</h3>
          <p>查看专属优惠券与抵扣权益</p>
          <span className="member-go">查看详情 →</span>
        </button>
      </div>

      <div className="account-body">
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={[
            {
              key: 'paid',
              label: `我的付费服务 (${paidServices ? paidServices.length : 0})`,
              children: paidPane,
            },
            {
              key: 'coupons',
              label: `我的优惠券 (${coupons.length})`,
              children: couponsPane,
            },
          ]}
        />
      </div>
    </div>
  );
}

export default Account;
